import React, { useEffect, useRef, useState } from 'react';

export interface ReadMoreProps {
  children: React.ReactNode;
  maxHeight?: number;
  maxWidth?: number;
  defaultExpanded?: boolean;
  className?: string;
  onToggle?(expanded: boolean): void;
}

const ReadMore: React.FC<ReadMoreProps> = ({
  children,
  maxHeight = 80,
  maxWidth = 400,
  defaultExpanded = false,
  className,
  onToggle,
}) => {
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const [showToggle, setShowToggle] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkOverflow = () => {
      if (!expanded) {
        const prev = el.style.maxHeight;
        el.style.maxHeight = `${maxHeight}px`;
        const needed = el.scrollHeight > el.clientHeight + 1; // +1 for rounding
        el.style.maxHeight = prev;
        setShowToggle(needed);
      } else {
        setShowToggle(el.scrollHeight > maxHeight);
      }
    };

    checkOverflow();

    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children, expanded, maxHeight]);

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      onToggle?.(next);
      return next;
    });
  };

  const containerStyle: React.CSSProperties = {
    maxWidth,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    overflow: 'hidden',
    transition: 'max-height 0.25s ease',
    maxHeight: expanded ? 10000 : maxHeight, // large max when expanded
    position: 'relative',
  };

  const buttonStyle: React.CSSProperties = {
    marginTop: 4,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#44c082',
    font: 'inherit',
  };

  const fadeStyle: React.CSSProperties = {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: expanded ? 0 : 24,
    background:
      'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
    pointerEvents: 'none',
  };

  return (
    <div className={className}>
      <div ref={contentRef} style={containerStyle}>
        {children}
        {!expanded && showToggle && (
          <div style={fadeStyle as React.CSSProperties} />
        )}
      </div>

      {showToggle && (
        <button
          type="button"
          onClick={toggle}
          style={buttonStyle}
          aria-expanded={expanded}
          aria-controls="read-more-content"
        >
          {expanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
        </button>
      )}
    </div>
  );
};

export default ReadMore;
